import React, { useState, useEffect } from 'react';
import {
  getAnalyticsData
} from '../../utils/visitorTracking';
import {
  getTotalRegisterClicks,
  getRegisterClicksForTrackedPages
} from '../../utils/registerClickTracking';
import Pagination from '../../components/Pagination/Pagination';
import './VisitorAnalytics.scss';

const VisitorAnalytics = () => {
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [uniqueVisitors, setUniqueVisitors] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [visitorsByIP, setVisitorsByIP] = useState([]);
  const [allVisits, setAllVisits] = useState([]);
  const [totalRegisterClicks, setTotalRegisterClicks] = useState(0);
  const [registerClicksByPage, setRegisterClicksByPage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination states for Routes
  const [routesPage, setRoutesPage] = useState(1);
  const [routesPerPage, setRoutesPerPage] = useState(10);
  
  // Pagination states for IP Visitors
  const [ipPage, setIpPage] = useState(1);
  const [ipPerPage, setIpPerPage] = useState(10);
  
  // Pagination states for Recent Visits
  const [visitsPage, setVisitsPage] = useState(1);
  const [visitsPerPage, setVisitsPerPage] = useState(10);

  useEffect(() => {
    loadData();
    // Refresh data every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [analyticsData, totalClicks, clicksByPage] = await Promise.all([
        getAnalyticsData(),
        getTotalRegisterClicks(),
        getRegisterClicksForTrackedPages()
      ]);
      
      setTotalVisitors(analyticsData.totalVisitors || 0);
      setUniqueVisitors(analyticsData.uniqueVisitors || 0);
      setRoutes(analyticsData.routesVisited || []);
      setVisitorsByIP(analyticsData.visitorsByIP || []);
      setAllVisits(analyticsData.allVisits || []); // Already reversed from server
      setTotalRegisterClicks(totalClicks);
      setRegisterClicksByPage(clicksByPage);
      setLoading(false);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      setError('Failed to load analytics data. Please check if the server is running.');
      setLoading(false);
    }
  };

  // Calculate pagination for routes
  const routesStartIndex = (routesPage - 1) * routesPerPage;
  const routesEndIndex = routesStartIndex + routesPerPage;
  const paginatedRoutes = routes.slice(routesStartIndex, routesEndIndex);
  const routesTotalPages = Math.ceil(routes.length / routesPerPage);

  // Calculate pagination for IP visitors
  const ipStartIndex = (ipPage - 1) * ipPerPage;
  const ipEndIndex = ipStartIndex + ipPerPage;
  const paginatedIPs = visitorsByIP.slice(ipStartIndex, ipEndIndex);
  const ipTotalPages = Math.ceil(visitorsByIP.length / ipPerPage);

  // Calculate pagination for recent visits
  const visitsStartIndex = (visitsPage - 1) * visitsPerPage;
  const visitsEndIndex = visitsStartIndex + visitsPerPage;
  const paginatedVisits = allVisits.slice(visitsStartIndex, visitsEndIndex);
  const visitsTotalPages = Math.ceil(allVisits.length / visitsPerPage);

  if (loading) {
    return <div className="visitor-analytics-loading">Loading analytics data...</div>;
  }

  if (error) {
    return (
      <div className="visitor-analytics">
        <div className="analytics-error" style={{ padding: '20px', textAlign: 'center', color: '#ff4444' }}>
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={loadData} style={{ marginTop: '10px', padding: '10px 20px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="visitor-analytics">
      <div className="analytics-header">
        <h2>Visitor Analytics</h2>
        <p className="subtitle">Track and monitor your website visitors</p>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card card-primary">
          <div className="card-background"></div>
          <div className="card-content-wrapper">
            <div className="card-icon">
              <i className="ti-user"></i>
            </div>
            <div className="card-content">
              <h3>{totalVisitors.toLocaleString()}</h3>
              <p>Total Visits</p>
              <div className="card-trend">
                <i className="ti-arrow-up"></i>
                <span>All time</span>
              </div>
            </div>
          </div>
        </div>
        <div className="summary-card card-secondary">
          <div className="card-background"></div>
          <div className="card-content-wrapper">
            <div className="card-icon">
              <i className="ti-id-badge"></i>
            </div>
            <div className="card-content">
              <h3>{uniqueVisitors.toLocaleString()}</h3>
              <p>Unique Visitors</p>
              <div className="card-trend">
                <i className="ti-users"></i>
                <span>Distinct IPs</span>
              </div>
            </div>
          </div>
        </div>
        <div className="summary-card card-tertiary">
          <div className="card-background"></div>
          <div className="card-content-wrapper">
            <div className="card-icon">
              <i className="ti-location-pin"></i>
            </div>
            <div className="card-content">
              <h3>{routes.length}</h3>
              <p>Routes Visited</p>
              <div className="card-trend">
                <i className="ti-map"></i>
                <span>Active routes</span>
              </div>
            </div>
          </div>
        </div>
        <div className="summary-card card-register">
          <div className="card-background"></div>
          <div className="card-content-wrapper">
            <div className="card-icon">
              <i className="ti-hand-point-up"></i>
            </div>
            <div className="card-content">
              <h3>{totalRegisterClicks.toLocaleString()}</h3>
              <p>Register Now Clicks</p>
              <div className="card-trend">
                <i className="ti-mouse"></i>
                <span>
                  {registerClicksByPage.length > 0 
                    ? `${registerClicksByPage.length} page${registerClicksByPage.length > 1 ? 's' : ''}`
                    : 'No clicks yet'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Register Now Clicks Breakdown */}
      {registerClicksByPage.length > 0 && (
        <div className="analytics-section register-clicks-section">
          <h3 className="section-title">
            <i className="ti-hand-point-up"></i> Register Now Clicks by Page
          </h3>
          <div className="register-clicks-grid">
            {registerClicksByPage.map((page, index) => {
              const pageNames = {
                'home': 'Home',
                'about': 'About',
                'summit-objective': 'Summit Objective'
              };
              return (
                <div key={index} className="register-click-item">
                  <div className="click-item-header">
                    <span className="page-name">{pageNames[page.page] || page.page}</span>
                    <span className="click-count">{page.count}</span>
                  </div>
                  <div className="click-item-bar">
                    <div 
                      className="click-item-progress" 
                      style={{ 
                        width: `${(page.count / totalRegisterClicks) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Routes Statistics */}
      <div className="analytics-section">
        <h3 className="section-title">
          <i className="ti-map"></i> Routes Visited
        </h3>
        <div className="routes-table">
          <table>
            <thead>
              <tr>
                <th>Route</th>
                <th>Visits</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRoutes.length > 0 ? (
                paginatedRoutes.map((item, index) => {
                  const percentage = totalVisitors > 0 ? ((item.count / totalVisitors) * 100).toFixed(1) : 0;
                  return (
                    <tr key={routesStartIndex + index}>
                      <td>
                        <i className="ti-link"></i> {item.route}
                      </td>
                      <td>{item.count}</td>
                      <td>
                        <div className="percentage-bar">
                          <div
                            className="percentage-fill"
                            style={{ width: `${percentage}%` }}
                          ></div>
                          <span>{percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3" className="no-data">No route data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {routes.length > 0 && (
          <Pagination
            currentPage={routesPage}
            totalPages={routesTotalPages}
            onPageChange={setRoutesPage}
            itemsPerPage={routesPerPage}
            totalItems={routes.length}
            onItemsPerPageChange={(value) => {
              setRoutesPerPage(value);
              setRoutesPage(1);
            }}
          />
        )}
      </div>

      {/* Visitors by IP */}
      <div className="analytics-section">
        <h3 className="section-title">
          <i className="ti-server"></i> Visitors by IP Address
        </h3>
        <div className="ip-table">
          <table>
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Total Visits</th>
                <th>Routes</th>
                <th>Last Visit</th>
              </tr>
            </thead>
            <tbody>
              {paginatedIPs.length > 0 ? (
                paginatedIPs.map((visitor, index) => (
                  <tr key={ipStartIndex + index}>
                    <td>
                      <i className="ti-world"></i> {visitor.ip}
                    </td>
                    <td>{visitor.totalVisits}</td>
                    <td>
                      <div className="routes-list">
                        {visitor.routes.slice(0, 3).map((route, i) => (
                          <span key={i} className="route-tag">
                            {route}
                          </span>
                        ))}
                        {visitor.routes.length > 3 && (
                          <span className="route-tag more">
                            +{visitor.routes.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {visitor.visits[visitor.visits.length - 1]?.date || 'N/A'}
                      <br />
                      <small>{visitor.visits[visitor.visits.length - 1]?.time || ''}</small>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">No visitor data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {visitorsByIP.length > 0 && (
          <Pagination
            currentPage={ipPage}
            totalPages={ipTotalPages}
            onPageChange={setIpPage}
            itemsPerPage={ipPerPage}
            totalItems={visitorsByIP.length}
            onItemsPerPageChange={(value) => {
              setIpPerPage(value);
              setIpPage(1);
            }}
          />
        )}
      </div>

      {/* Recent Visits */}
      <div className="analytics-section">
        <h3 className="section-title">
          <i className="ti-time"></i> Recent Visits
        </h3>
        <div className="recent-visits">
          {paginatedVisits.length > 0 ? (
            <>
              <div className="visits-list">
                {paginatedVisits.map((visit, index) => (
                  <div key={visitsStartIndex + index} className="visit-item">
                    <div className="visit-info">
                      <div className="visit-route">
                        <i className="ti-link"></i> {visit.route}
                      </div>
                      <div className="visit-ip">
                        <i className="ti-world"></i> {visit.ip}
                      </div>
                    </div>
                    <div className="visit-time">
                      <div>{visit.date}</div>
                      <small>{visit.time}</small>
                    </div>
                  </div>
                ))}
              </div>
              <Pagination
                currentPage={visitsPage}
                totalPages={visitsTotalPages}
                onPageChange={setVisitsPage}
                itemsPerPage={visitsPerPage}
                totalItems={allVisits.length}
                onItemsPerPageChange={(value) => {
                  setVisitsPerPage(value);
                  setVisitsPage(1);
                }}
              />
            </>
          ) : (
            <p className="no-data">No recent visits</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalytics;

